import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Activity, X, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

function todayIso() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function formatIstParts(iso) {
  // Convert ISO UTC timestamp to IST date + time strings.
  if (!iso) return { d: '', t: '' };
  try {
    const dt = new Date(iso);
    const istMs = dt.getTime() + 5.5 * 60 * 60 * 1000;
    const ist = new Date(istMs);
    const pad = (n) => String(n).padStart(2, '0');
    return {
      d: `${ist.getUTCFullYear()}-${pad(ist.getUTCMonth() + 1)}-${pad(ist.getUTCDate())}`,
      t: `${pad(ist.getUTCHours())}:${pad(ist.getUTCMinutes())}:${pad(ist.getUTCSeconds())}`,
    };
  } catch {
    return { d: '', t: '' };
  }
}

export default function ComboPPStaffPerformance() {
  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState(todayIso());
  const [toDate, setToDate] = useState(todayIso());
  const [flow, setFlow] = useState('ALL');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ rows: [], totals: { combos_assembled: 0, components_used: 0, staff_count: 0, v1_combos: 0, v2_combos: 0 } });
  const [drawer, setDrawer] = useState(null); // {staff, type:'combos'|'components', items, loading}

  const authHeaders = useMemo(() => {
    const tk = localStorage.getItem('session_token');
    return tk ? { Authorization: `Bearer ${tk}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: fromDate, to: toDate };
      if (flow !== 'ALL') params.flow = flow;
      const r = await axios.get(`${API}/api/combo-reconciliation/pp-staff-performance`, {
        params,
        headers: authHeaders,
      });
      setData({ rows: r.data?.rows || [], totals: r.data?.totals || {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, flow, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const openDrawer = async (staff, type) => {
    setDrawer({ staff, type, loading: true, items: [] });
    try {
      const path = type === 'combos' ? 'combos' : 'components';
      const params = { staff_id: staff.staff_id || '__unassigned__', from: fromDate, to: toDate };
      if (flow !== 'ALL') params.flow = flow;
      const r = await axios.get(
        `${API}/api/combo-reconciliation/pp-staff-performance/${path}`,
        { params, headers: authHeaders }
      );
      const items = r.data?.[type === 'combos' ? 'items' : 'components'] || [];
      setDrawer({ staff, type, loading: false, items });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load details');
      setDrawer((d) => (d ? { ...d, loading: false, items: [] } : d));
    }
  };

  const downloadCsv = async (path, filename, params) => {
    try {
      const r = await axios.get(`${API}/api/combo-reconciliation/${path}`, {
        params,
        headers: authHeaders,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV export failed');
    }
  };

  const exportSummaryCsv = () => {
    const params = { from: fromDate, to: toDate };
    if (flow !== 'ALL') params.flow = flow;
    downloadCsv(
      'pp-staff-performance/export-csv',
      `pp_staff_performance_${fromDate}_${toDate}_${flow}.csv`,
      params,
    );
  };

  const exportAllCombosCsv = () => {
    const params = { from: fromDate, to: toDate };
    if (flow !== 'ALL') params.flow = flow;
    toast.info('Building combined CSV (this may take a moment)…');
    downloadCsv(
      'pp-staff-performance/all-combos/export-csv',
      `pp_staff_all_combos_${fromDate}_${toDate}_${flow}.csv`,
      params,
    );
  };

  const exportDrawerCsv = () => {
    if (!drawer) return;
    const params = {
      staff_id: drawer.staff.staff_id || '__unassigned__',
      from: fromDate,
      to: toDate,
    };
    if (flow !== 'ALL') params.flow = flow;
    const path = `pp-staff-performance/${drawer.type}/export-csv`;
    const safeStaff = (drawer.staff.staff_id || 'unassigned').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadCsv(
      path,
      `pp_staff_${drawer.type}_${safeStaff}_${fromDate}_${toDate}.csv`,
      params,
    );
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5] p-6" data-testid="combo-pp-staff-performance">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="outline" size="sm" onClick={() => navigate('/wms/combo-v2')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="bg-indigo-600 text-white p-2 rounded">
              <Activity className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900">Combo PP Staff Performance</h1>
              <p className="text-sm text-stone-500">Combos assembled and components used per staff (V1 + V2)</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="text-xs text-stone-500 mb-1 block">From</label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="filter-from-date"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">To</label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="filter-to-date"
              />
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Flow</label>
              <Tabs value={flow} onValueChange={setFlow}>
                <TabsList className="grid grid-cols-3 w-full" data-testid="flow-toggle">
                  <TabsTrigger value="ALL" data-testid="flow-all">All</TabsTrigger>
                  <TabsTrigger value="V1" data-testid="flow-v1">V1</TabsTrigger>
                  <TabsTrigger value="V2" data-testid="flow-v2">V2</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <Button onClick={load} disabled={loading} data-testid="apply-filter-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Apply
            </Button>
          </div>
          <div className="flex justify-end mt-3 gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportSummaryCsv}
              disabled={loading || !data.rows.length}
              data-testid="export-summary-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export Summary CSV
            </Button>
            <Button
              size="sm"
              onClick={exportAllCombosCsv}
              disabled={loading || !data.rows.length}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="export-all-combos-csv-btn"
            >
              <Download className="w-4 h-4 mr-1" /> Export All Staff Combos CSV
            </Button>
          </div>
        </Card>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <Card className="p-3" data-testid="total-staff">
            <div className="text-xs uppercase tracking-wide text-stone-500">Staff</div>
            <div className="text-2xl font-bold">{data.totals.staff_count || 0}</div>
          </Card>
          <Card className="p-3" data-testid="total-combos">
            <div className="text-xs uppercase tracking-wide text-stone-500">Combos Assembled</div>
            <div className="text-2xl font-bold">{data.totals.combos_assembled || 0}</div>
            <div className="text-[10px] text-stone-500 mt-1">V1: {data.totals.v1_combos || 0} · V2: {data.totals.v2_combos || 0}</div>
          </Card>
          <Card className="p-3" data-testid="total-components">
            <div className="text-xs uppercase tracking-wide text-stone-500">Components Used</div>
            <div className="text-2xl font-bold">{data.totals.components_used || 0}</div>
          </Card>
          <Card className="p-3" data-testid="total-rate">
            <div className="text-xs uppercase tracking-wide text-stone-500">Avg Comp / Combo</div>
            <div className="text-2xl font-bold">
              {data.totals.combos_assembled
                ? (data.totals.components_used / data.totals.combos_assembled).toFixed(2)
                : '0.00'}
            </div>
          </Card>
        </div>

        {/* Table */}
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="staff-performance-table">
            <thead className="bg-stone-100">
              <tr className="border-b border-stone-200">
                <th className="text-left p-2 w-8">#</th>
                <th className="text-left p-2">Staff</th>
                <th className="text-right p-2">Combos Assembled</th>
                <th className="text-right p-2">Components Used</th>
                <th className="text-right p-2">V1</th>
                <th className="text-right p-2">V2</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-stone-500"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-stone-500" data-testid="no-data-row">No combos assembled in this window.</td></tr>
              ) : data.rows.map((r, i) => {
                const rowKey = r.staff_id || '__unassigned__';
                return (
                <tr key={rowKey} className="border-t border-stone-200" data-testid={`staff-row-${rowKey}`}>
                  <td className="p-2 text-stone-500">{i + 1}</td>
                  <td className="p-2">
                    <div className="font-medium">{r.staff_name || r.staff_id || 'Unassigned'}</div>
                    <div className="text-xs text-stone-500 font-mono">{r.staff_id || '—'}</div>
                  </td>
                  <td className="p-2 text-right">
                    <button
                      className="text-emerald-700 font-semibold hover:underline"
                      onClick={() => openDrawer(r, 'combos')}
                      data-testid={`combos-count-${rowKey}`}
                    >
                      {r.combos_assembled}
                    </button>
                  </td>
                  <td className="p-2 text-right">
                    <button
                      className="text-indigo-700 font-semibold hover:underline"
                      onClick={() => openDrawer(r, 'components')}
                      data-testid={`components-count-${rowKey}`}
                    >
                      {r.components_used}
                    </button>
                  </td>
                  <td className="p-2 text-right text-stone-600">{r.v1_combos}</td>
                  <td className="p-2 text-right text-stone-600">{r.v2_combos}</td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      </div>

      {/* Drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setDrawer(null)}>
          <div
            className="bg-white w-full max-w-2xl h-full overflow-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="staff-performance-drawer"
          >
            <div className="sticky top-0 bg-white border-b border-stone-200 p-4 flex justify-between items-center">
              <div>
                <div className="font-semibold">
                  {drawer.staff.staff_name || drawer.staff.staff_id || 'Unassigned'} —{' '}
                  {drawer.type === 'combos' ? 'Combos Assembled' : 'Components Used'}
                </div>
                <div className="text-xs text-stone-500">
                  {fromDate} → {toDate} · {drawer.items.length} record{drawer.items.length !== 1 ? 's' : ''}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDrawer(null)} data-testid="drawer-close-btn">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="px-4 pt-3 flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={exportDrawerCsv}
                disabled={drawer.loading || !drawer.items.length}
                data-testid="export-drawer-csv-btn"
              >
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            </div>
            <div className="p-4">
              {drawer.loading ? (
                <div className="text-center text-stone-500 py-10">
                  <Loader2 className="inline w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
              ) : drawer.items.length === 0 ? (
                <div className="text-center text-stone-500 py-10" data-testid="drawer-empty">
                  No records in this window.
                </div>
              ) : drawer.type === 'combos' ? (
                <table className="w-full text-xs" data-testid="drawer-combos-table">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-1.5">Combo USN</th>
                      <th className="text-left p-1.5">Combo</th>
                      <th className="text-left p-1.5">Description</th>
                      <th className="text-left p-1.5">Design / Shade / Size</th>
                      <th className="text-left p-1.5">Flow</th>
                      <th className="text-left p-1.5">Assembled (Date)</th>
                      <th className="text-left p-1.5">Time (IST)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawer.items.map((it) => {
                      const ist = it.assembled_at ? formatIstParts(it.assembled_at) : { d: '—', t: '—' };
                      return (
                        <tr key={it.combo_usn} className="border-t border-stone-200">
                          <td className="p-1.5 font-mono">{it.combo_usn}</td>
                          <td className="p-1.5">{it.combo_name || it.combo_barcode}</td>
                          <td className="p-1.5">{it.description || '—'}</td>
                          <td className="p-1.5">{[it.design_name, it.shade, it.size].filter(Boolean).join(' / ')}</td>
                          <td className="p-1.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.flow_label === 'V2' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {it.flow_label || ((it.flow || '').toLowerCase() === 'v2' ? 'V2' : 'V1')}
                            </span>
                          </td>
                          <td className="p-1.5 font-mono">{ist.d}</td>
                          <td className="p-1.5 font-mono">{ist.t}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-xs" data-testid="drawer-components-table">
                  <thead className="bg-stone-100">
                    <tr>
                      <th className="text-left p-1.5">USN / Component</th>
                      <th className="text-left p-1.5">Bundle</th>
                      <th className="text-left p-1.5">SKU</th>
                      <th className="text-left p-1.5">Session</th>
                      <th className="text-left p-1.5">Flow</th>
                      <th className="text-left p-1.5">Consumed (Date)</th>
                      <th className="text-left p-1.5">Time (IST)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drawer.items.map((it, i) => {
                      const ist = it.consumed_at ? formatIstParts(it.consumed_at) : { d: '—', t: '—' };
                      return (
                        <tr key={`${it.usn_code || it.combo_usn}-${i}`} className="border-t border-stone-200">
                          <td className="p-1.5 font-mono">{it.usn_code || it.component_name || '—'}</td>
                          <td className="p-1.5 font-mono">{it.bundle_code || '—'}</td>
                          <td className="p-1.5 font-mono">{it.sku_code || '—'}</td>
                          <td className="p-1.5 font-mono text-[10px]">{it.session_id || '—'}</td>
                          <td className="p-1.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.flow === 'V2' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                              {it.flow}
                            </span>
                          </td>
                          <td className="p-1.5 font-mono">{ist.d}</td>
                          <td className="p-1.5 font-mono">{ist.t}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
